import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import api from '../api';

function toPublicUrl(baseUrl, storedUrl) {
  const p = String(storedUrl || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(baseUrl || '').replace(/\/$/, '');
  const rel = p.startsWith('/') ? p : `/${p}`;
  return base + rel;
}

function isPrivileged(user) {
  const role = String(user?.role || '').toLowerCase();
  const roles = Array.isArray(user?.roles) ? user.roles.map(r => String(r || '').toLowerCase()) : [];
  return role === 'admin' || role === 'moderator' || role === 'owner' || roles.includes('admin') || roles.includes('moderator') || roles.includes('owner');
}

export default function APNClipsVoting() {
  const { user } = useContext(UserContext);
  const baseUrl = useMemo(() => (import.meta.env.VITE_API_URL || 'http://localhost:5000'), []);
  const canSeeCounts = isPrivileged(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyVoteId, setBusyVoteId] = useState(null);

  const [openId, setOpenId] = useState(null);
  const [countsById, setCountsById] = useState({});
  const [countsLoadingId, setCountsLoadingId] = useState(null);

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/apn-clips/voting/submissions');
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
    } catch (e) {
      setError(e?.response?.data?.error || 'Konnte Voting-Clips nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setVote = async (id, value) => {
    if (!id) return;
    if (!['up', 'neutral', 'down'].includes(String(value))) return;
    setBusyVoteId(id);
    setError('');
    try {
      const res = await api.post(`/apn-clips/voting/submissions/${id}/vote`, { value });
      const userVote = res.data?.userVote || value;
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, userVote } : x)));
    } catch (e) {
      setError(e?.response?.data?.error || 'Vote speichern fehlgeschlagen.');
    } finally {
      setBusyVoteId(null);
    }
  };

  const loadCounts = async (id) => {
    if (!canSeeCounts || !id) return;
    setCountsLoadingId(id);
    setError('');
    try {
      const res = await api.get(`/apn-clips/voting/submissions/${id}/votes/summary`);
      setCountsById((prev) => ({ ...prev, [id]: res.data }));
    } catch (e) {
      setError(e?.response?.data?.error || 'Konnte Vote-Zahlen nicht laden.');
    } finally {
      setCountsLoadingId(null);
    }
  };

  const openItem = useMemo(() => items.find((x) => x.id === openId) || null, [items, openId]);
  const openVideoUrl = useMemo(() => (openItem ? toPublicUrl(baseUrl, openItem.storedUrl) : ''), [openItem, baseUrl]);

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Community Voting</h2>
        <p className="hero-desc" style={{ margin: '0 auto 18px', textAlign: 'center', maxWidth: 920 }}>
          Vote für freigegebene Clips aus der Community.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0 14px' }}>
          <Link className="hero-btn-secondary" to="/apn-clips" style={{ textDecoration: 'none' }}>Zurück</Link>
          <button className="hero-btn-primary" type="button" onClick={load} disabled={loading}>Neu laden</button>
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Clips</h2>

          {error && (
            <div style={{ padding: '0 8px 12px', color: '#ffdddd', fontSize: 13 }}>{error}</div>
          )}

          {loading ? (
            <div style={{ padding: '0 8px 8px', opacity: 0.8 }}>Lade…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '0 8px 8px', opacity: 0.8 }}>Aktuell sind keine freigegebenen Clips zum Voten vorhanden.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, padding: '0 8px 8px' }}>
              {items.map((s) => {
                const id = s?.id;
                const vote = String(s?.userVote || '');
                const thumb = String(s?.thumbnailUrl || '').trim();
                const title = String(s?.title || '(ohne Titel)');
                const creator = String(s?.creatorName || '—');
                const platform = String(s?.platform || '—');
                const category = String(s?.category || '—');
                const counts = countsById?.[id] || null;

                return (
                  <div key={id} className="highlight-card" style={{ ...card, alignItems: 'stretch', display: 'grid', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setOpenId(id)}
                      style={{
                        padding: 0,
                        border: '1px solid rgba(255,255,255,0.10)',
                        borderRadius: 12,
                        overflow: 'hidden',
                        background: 'transparent',
                        cursor: 'pointer',
                      }}
                      title="Clip ansehen"
                    >
                      {thumb ? (
                        <img
                          alt={title}
                          src={thumb}
                          style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <div style={{ height: 150, display: 'grid', placeItems: 'center', opacity: 0.8 }}>
                          Kein Thumbnail
                        </div>
                      )}
                    </button>

                    <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25 }}>{title}</div>
                    <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.35 }}>
                      <div><b>Creator:</b> {creator}</div>
                      <div><b>Plattform:</b> {platform}</div>
                      <div><b>Kategorie:</b> {category}</div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={vote === 'up' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                        disabled={busyVoteId === id}
                        onClick={() => setVote(id, 'up')}
                      >
                        👍
                      </button>
                      <button
                        type="button"
                        className={vote === 'neutral' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                        disabled={busyVoteId === id}
                        onClick={() => setVote(id, 'neutral')}
                      >
                        😐
                      </button>
                      <button
                        type="button"
                        className={vote === 'down' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                        disabled={busyVoteId === id}
                        onClick={() => setVote(id, 'down')}
                      >
                        👎
                      </button>
                    </div>

                    {canSeeCounts && (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <button
                          type="button"
                          className="hero-btn-secondary"
                          onClick={() => loadCounts(id)}
                          disabled={countsLoadingId === id}
                        >
                          Vote-Zahlen anzeigen
                        </button>
                        {counts && (
                          <div style={{ fontSize: 12, opacity: 0.9 }}>
                            <div><b>Gesamt:</b> {counts.total}</div>
                            <div><b>👍:</b> {counts.counts?.up ?? 0} &nbsp; <b>😐:</b> {counts.counts?.neutral ?? 0} &nbsp; <b>👎:</b> {counts.counts?.down ?? 0}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {openItem && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpenId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="highlight-card"
            style={{
              ...card,
              width: '100%',
              maxWidth: 980,
              alignItems: 'stretch',
              display: 'grid',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>
                {String(openItem?.title || '(ohne Titel)')}
              </div>
              <button type="button" className="hero-btn-secondary" onClick={() => setOpenId(null)}>Schließen</button>
            </div>

            {openVideoUrl ? (
              <video
                controls
                src={openVideoUrl}
                style={{ width: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }}
              />
            ) : (
              <div style={{ opacity: 0.85, fontSize: 13 }}>Video ist nicht verfügbar.</div>
            )}

            <div style={{ fontSize: 13, opacity: 0.9, display: 'grid', gap: 4 }}>
              <div><b>Creator:</b> {String(openItem?.creatorName || '—')}</div>
              <div><b>Plattform:</b> {String(openItem?.platform || '—')}</div>
              <div><b>Kategorie:</b> {String(openItem?.category || '—')}</div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className={String(openItem?.userVote) === 'up' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                disabled={busyVoteId === openItem.id}
                onClick={() => setVote(openItem.id, 'up')}
              >
                👍 Daumen hoch
              </button>
              <button
                type="button"
                className={String(openItem?.userVote) === 'neutral' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                disabled={busyVoteId === openItem.id}
                onClick={() => setVote(openItem.id, 'neutral')}
              >
                😐 Egal
              </button>
              <button
                type="button"
                className={String(openItem?.userVote) === 'down' ? 'hero-btn-primary' : 'hero-btn-secondary'}
                disabled={busyVoteId === openItem.id}
                onClick={() => setVote(openItem.id, 'down')}
              >
                👎 Daumen runter
              </button>
            </div>

            {canSeeCounts && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="hero-btn-secondary"
                  onClick={() => loadCounts(openItem.id)}
                  disabled={countsLoadingId === openItem.id}
                >
                  Vote-Zahlen anzeigen
                </button>
                {countsById?.[openItem.id] && (
                  <div style={{ fontSize: 12, opacity: 0.9 }}>
                    <b>Gesamt:</b> {countsById[openItem.id].total} &nbsp; | &nbsp;
                    <b>👍</b> {countsById[openItem.id].counts?.up ?? 0} &nbsp;
                    <b>😐</b> {countsById[openItem.id].counts?.neutral ?? 0} &nbsp;
                    <b>👎</b> {countsById[openItem.id].counts?.down ?? 0}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
